import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Ban, CheckCircle2, Gift } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { brl, fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Navigate } from "react-router-dom";

const ESCROW_LABEL = {
  checkout_criado: "Aguardando pagamento", pago_retido: "Em custódia",
  liberacao_pendente: "Repasse pendente", liberado: "Liberado",
  reembolso_pendente: "Reembolso em andamento", reembolsado: "Reembolsado", cancelado: "Cancelado",
};
const DISPUTE_LABEL = {
  aberta: "Aberta", em_analise: "Em análise", resolvida_reembolso: "Resolvida — reembolso",
  resolvida_liberacao: "Resolvida — liberada ao especialista", encerrada: "Encerrada",
};

export default function Admin() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [supportWa, setSupportWa] = useState("");
  const [search, setSearch] = useState("");
  const [contribs, setContribs] = useState([]);
  const [params, setParams] = useState(null);
  const [plans, setPlans] = useState([]);
  const [coverage, setCoverage] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [payInfo, setPayInfo] = useState(null);
  const [pendingProducts, setPendingProducts] = useState([]);
  const [prodContribs, setProdContribs] = useState([]);
  const [matRules, setMatRules] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [escrowPay, setEscrowPay] = useState([]);
  const [escrowCfg, setEscrowCfg] = useState(null);
  const [assistSpecs, setAssistSpecs] = useState([]);
  const [disputes, setDisputes] = useState([]);
  const [infractions, setInfractions] = useState([]);

  const loadAll = () => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/users", { params: { search } }).then((r) => setUsers(r.data)).catch(() => {});
    api.get("/admin/platform-config").then((r) => setSupportWa(r.data?.support_whatsapp || "")).catch(() => {});
    api.get("/admin/contributions", { params: { status: "flagged" } }).then((r) => setContribs(r.data)).catch(() => {});
    api.get("/admin/regional-params").then((r) => setParams(r.data)).catch(() => {});
    api.get("/plans").then((r) => setPlans(r.data)).catch(() => {});
    api.get("/admin/coverage").then((r) => setCoverage(r.data)).catch(() => {});
    api.get("/admin/directory").then((r) => setProfiles(r.data)).catch(() => {});
    api.get("/payments/config-info").then((r) => setPayInfo(r.data)).catch(() => {});
    api.get("/admin/products/pending").then((r) => setPendingProducts(r.data)).catch(() => {});
    api.get("/admin/product-contributions", { params: { status: "em_analise" } }).then((r) => setProdContribs(r.data)).catch(() => {});
    api.get("/admin/material-rules").then((r) => setMatRules(r.data)).catch(() => {});
    api.get("/admin/reputation/leaderboard").then((r) => setLeaderboard(r.data)).catch(() => {});
    api.get("/admin/payouts").then((r) => setPayouts(r.data)).catch(() => {});
    api.get("/admin/escrow").then((r) => setEscrowPay(r.data)).catch(() => {});
    api.get("/admin/escrow/config").then((r) => setEscrowCfg(r.data)).catch(() => {});
    api.get("/admin/assist/specialists").then((r) => setAssistSpecs(r.data)).catch(() => {});
    api.get("/admin/disputes").then((r) => setDisputes(r.data)).catch(() => {});
    api.get("/admin/infractions").then((r) => setInfractions(r.data)).catch(() => {});
  };
  useEffect(() => { if (user?.role === "admin") loadAll(); }, [user]);

  if (user && user.role !== "admin") return <Navigate to="/app" />;
  if (!stats) return <div className="py-20 text-center text-slate-400">Carregando...</div>;

  const toggleComp = async (u, comped) => {
    try {
      await api.put(`/admin/users/${u.id}/comp`, { comped });
      toast.success(comped ? "Acesso total liberado (cortesia)." : "Cortesia removida.");
      loadAll();
    } catch (e) { toast.error(apiError(e)); }
  };

  const blockUser = async (u, blocked) => {
    await api.put(`/admin/users/${u.id}/block`, { blocked });
    toast.success(blocked ? "Usuário bloqueado." : "Usuário desbloqueado.");
    loadAll();
  };

  const changePlan = async (u, plan_id) => {
    await api.put(`/admin/users/${u.id}/plan`, { plan_id });
    toast.success("Plano alterado.");
    loadAll();
  };

  const review = async (c, status) => {
    try {
      await api.put(`/admin/contributions/${c.id}/review`, { status });
      toast.success(status === "approved" ? "Contribuição aprovada." : "Contribuição rejeitada.");
      loadAll();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const saveParams = async () => {
    try {
      await api.put("/admin/regional-params", {
        feature_enabled: !!params.feature_enabled,
        min_contributions: Number(params.min_contributions) || 3,
        validity_days: Number(params.validity_days) || 365,
        trim_pct: Number(params.trim_pct) || 10,
        suspicion_factor: Number(params.suspicion_factor) || 2.5,
      });
      toast.success("Parâmetros salvos (registrado em auditoria).");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const savePlan = async (p) => {
    try {
      await api.put(`/admin/plans/${p.id || p._id}`, {
        price_monthly: Number(p.price_monthly), price_yearly: Number(p.price_yearly), limits: p.limits || {},
      });
      toast.success(`Plano ${p.name} atualizado.`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const moderate = async (p, moderation) => {
    await api.put(`/admin/directory/${p.id}/moderate`, { moderation });
    toast.success("Moderação aplicada.");
    loadAll();
  };

  const reviewProduct = async (p, status) => {
    try {
      await api.put(`/admin/products/${p.id}/review`, { status });
      toast.success(status === "validada" ? "Material aprovado e publicado." : "Material rejeitado.");
      loadAll();
    } catch (e) { toast.error(apiError(e)); }
  };

  const reviewProdContrib = async (c, status) => {
    try {
      await api.put(`/admin/product-contributions/${c.id}/review`, { status });
      toast.success(status === "validada" ? "Contribuição aprovada." : "Contribuição rejeitada.");
      loadAll();
    } catch (e) { toast.error(apiError(e)); }
  };

  const saveMatRules = async () => {
    try {
      await api.put("/admin/material-rules", {
        tubos_por_instalacao: Number(matRules.tubos_por_instalacao) || 2,
        cabo_pp_extra_m: Number(matRules.cabo_pp_extra_m) || 3,
        cabo_pp_nota: matRules.cabo_pp_nota || "",
        barra_isolamento_m: Number(matRules.barra_isolamento_m) || 2,
        fixadores_suporte_condensadora: Number(matRules.fixadores_suporte_condensadora) || 4,
        fita_acabamento_rolos: Number(matRules.fita_acabamento_rolos) || 1,
      });
      toast.success("Regras de instalação salvas.");
    } catch (e) { toast.error(apiError(e)); }
  };

  const saveEscrowCfg = async () => {
    try {
      await api.put("/admin/escrow/config", { fee_pct: Number(escrowCfg.fee_pct) || 0 });
      toast.success("Comissão atualizada.");
    } catch (e) { toast.error(apiError(e)); }
  };

  const markPaid = async (p) => {
    const note = window.prompt(`Confirmar repasse de ${brl(p.expert_amount)} via Pix para ${p.expert_name}?\nChave Pix: ${p.pix_key || "(não informada)"}\n\nOpcional: informe o comprovante/ID da transferência.`);
    if (note === null) return;
    try {
      await api.put(`/admin/payouts/${p.id}/paid`, { note });
      toast.success("Repasse confirmado.");
      loadAll();
    } catch (e) { toast.error(apiError(e)); }
  };

  const refundEscrow = async (p) => {
    if (!window.confirm(`Reembolsar ${brl(p.amount)} para ${p.buyer_name}? O valor em custódia volta ao pagador pelo Mercado Pago.`)) return;
    try {
      await api.post(`/admin/escrow/${p.id}/refund`);
      toast.success("Reembolso solicitado ao Mercado Pago.");
      loadAll();
    } catch (e) { toast.error(apiError(e)); }
  };

  const toggleVerify = async (s, verified) => {
    try {
      await api.put(`/admin/assist/specialists/${s.id}/verify`, { verified });
      toast.success(verified ? "Especialista verificado." : "Verificação removida.");
      loadAll();
    } catch (e) { toast.error(apiError(e)); }
  };

  const toggleSpecActive = async (s, active) => {
    try {
      await api.put(`/admin/assist/specialists/${s.id}/active`, { active });
      toast.success(active ? "Perfil reativado na busca." : "Perfil desativado da busca.");
      loadAll();
    } catch (e) { toast.error(apiError(e)); }
  };

  const resolveDispute = async (d, action) => {
    const msgs = {
      reembolso: "reembolsar o comprador via Mercado Pago",
      liberacao: "liberar o valor ao especialista (vai para a fila de repasse)",
      encerrar: "encerrar a disputa sem movimentar o valor",
      em_analise: "marcar a disputa como em análise",
    };
    const note = window.prompt(`Confirmar: ${msgs[action]}?\n\nNota da resolução (fica no histórico):`, "");
    if (note === null) return;
    try {
      await api.put(`/admin/disputes/${encodeURIComponent(d.id)}/resolve`, { action, note });
      toast.success("Disputa atualizada.");
      loadAll();
    } catch (e) { toast.error(apiError(e)); }
  };

  const toggleChatBlock = async (u, blocked) => {
    try {
      await api.put(`/admin/users/${u.user_id}/chat-block`, { blocked });
      toast.success(blocked ? "Chat suspenso." : "Chat reativado.");
      loadAll();
    } catch (e) { toast.error(apiError(e)); }
  };

  const kpis = [
    ["Usuários", stats.usuarios_total], ["Novos no mês", stats.usuarios_novos_mes],
    ["Assinantes ativos", stats.assinantes_ativos], ["Gratuitos", stats.usuarios_gratuitos],
    ["MRR estimado", brl(stats.mrr)], ["Bloqueados", stats.usuarios_bloqueados],
    ["Orçamentos criados", stats.orcamentos_total], ["Contribuições", stats.contribuicoes_total],
    ["Sinalizadas", stats.contribuicoes_sinalizadas], ["Perfis no diretório", stats.perfis_diretorio],
    ["Denúncias abertas", stats.denuncias_abertas], ["Eventos", stats.eventos_total],
  ];

  return (
    <div className="space-y-5 animate-in-up" data-testid="admin-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <ShieldCheck className="h-6 w-6 text-amber-500" /> Administração da plataforma
        </h1>
        <p className="text-sm text-slate-500">Métricas, usuários, planos e moderação</p>
      </div>

      <Tabs defaultValue="visao">
        <TabsList className="w-full justify-start overflow-x-auto">
          <TabsTrigger value="visao" data-testid="admin-tab-visao">Visão geral</TabsTrigger>
          <TabsTrigger value="usuarios" data-testid="admin-tab-usuarios">Usuários</TabsTrigger>
          <TabsTrigger value="contribuicoes" data-testid="admin-tab-contribuicoes">Contribuições ({contribs.length})</TabsTrigger>
          <TabsTrigger value="parametros" data-testid="admin-tab-parametros">Parâmetros regionais</TabsTrigger>
          <TabsTrigger value="planos" data-testid="admin-tab-planos">Planos</TabsTrigger>
          <TabsTrigger value="diretorio" data-testid="admin-tab-diretorio">Diretório</TabsTrigger>
          <TabsTrigger value="materiais" data-testid="admin-tab-materiais">Materiais ({pendingProducts.length + prodContribs.length})</TabsTrigger>
          <TabsTrigger value="custodia" data-testid="admin-tab-custodia">Custódia ({payouts.length})</TabsTrigger>
          <TabsTrigger value="assist" data-testid="admin-tab-assist">Assist ({assistSpecs.length})</TabsTrigger>
          <TabsTrigger value="disputas" data-testid="admin-tab-disputas">Disputas ({disputes.filter((d) => ["aberta", "em_analise"].includes(d.status)).length})</TabsTrigger>
          <TabsTrigger value="pagamentos" data-testid="admin-tab-pagamentos">Pagamentos</TabsTrigger>
        </TabsList>

        <TabsContent value="visao" className="mt-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {kpis.map(([l, v]) => (
              <Card key={l} className="border-slate-200">
                <CardContent className="p-4">
                  <p className="text-xs font-mono-num uppercase tracking-widest text-slate-500">{l}</p>
                  <p className="mt-1 font-mono-num text-xl font-semibold">{v}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          <Card className="border-slate-200 mt-4">
            <CardHeader><CardTitle className="text-base">Cobertura da base de preços</CardTitle></CardHeader>
            <CardContent className="space-y-1.5">
              {coverage.length === 0 && <p className="text-sm text-slate-500">Sem contribuições ainda.</p>}
              {coverage.slice(0, 15).map((c, i) => (
                <p key={i} className="text-sm text-slate-600 flex justify-between border-b border-slate-100 pb-1">
                  <span>{c.city}/{c.state} · {c.category}</span>
                  <span className="font-mono-num">{c.count} contrib.</span>
                </p>
              ))}
            </CardContent>
          </Card>
          <Card className="border-slate-200 mt-4">
            <CardHeader><CardTitle className="text-base">Suporte WhatsApp (botão flutuante no app)</CardTitle></CardHeader>
            <CardContent className="flex gap-2 items-center flex-wrap">
              <Input className="h-11 max-w-xs font-mono-num" placeholder="5511999999999" data-testid="admin-support-wa"
                     value={supportWa} onChange={(e) => setSupportWa(e.target.value)} />
              <Button className="h-11 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="admin-support-wa-save"
                      onClick={async () => {
                        try {
                          await api.put("/admin/platform-config", { support_whatsapp: supportWa });
                          toast.success("WhatsApp de suporte atualizado. O botão flutuante aparece para todos os usuários.");
                        } catch (e) { toast.error(apiError(e)); }
                      }}>Salvar</Button>
              <p className="text-[11px] text-slate-400 w-full">Número com DDI+DDD, só dígitos. Vazio desativa o botão.</p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="usuarios" className="mt-4 space-y-3">
          <div className="flex gap-2">
            <Input className="h-11" placeholder="Buscar por nome ou e-mail..." data-testid="admin-user-search"
                   value={search} onChange={(e) => setSearch(e.target.value)} />
            <Button className="h-11 bg-sky-600 hover:bg-sky-700 text-white" onClick={loadAll} data-testid="admin-user-search-btn">Buscar</Button>
          </div>
          {users.map((u) => (
            <Card key={u.id} className="border-slate-200" data-testid={`admin-user-${u.id}`}>
              <CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-medium text-sm">{u.name} {u.role === "admin" && <Badge className="ml-1 bg-amber-100 text-amber-700 border-amber-200 text-[10px]">ADMIN</Badge>} {u.comped && <Badge className="ml-1 bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px]" data-testid={`admin-comped-${u.id}`}>CORTESIA</Badge>}</div>
                  <p className="text-xs text-slate-500">{u.email} · desde {fmtDate(u.created_at)}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Select value={u.plan_id || "gratis"} onValueChange={(v) => changePlan(u, v)} disabled={u.role === "admin"}>
                    <SelectTrigger className="h-10 w-36" data-testid={`admin-plan-${u.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="gratis">Grátis</SelectItem>
                      <SelectItem value="profissional">Profissional</SelectItem>
                      <SelectItem value="empresa">Empresa</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button variant={u.comped ? "outline" : "ghost"} size="icon"
                          className={`h-10 w-10 ${u.comped ? "text-emerald-600" : "text-slate-400 hover:bg-emerald-50 hover:text-emerald-600"}`}
                          onClick={() => toggleComp(u, !u.comped)} disabled={u.role === "admin"}
                          data-testid={`admin-comp-${u.id}`} title={u.comped ? "Remover acesso cortesia" : "Liberar acesso total (cortesia)"}>
                    <Gift className="h-4 w-4" />
                  </Button>
                  <Button variant={u.blocked ? "outline" : "ghost"} size="icon"
                          className={`h-10 w-10 ${u.blocked ? "text-green-600" : "text-red-500 hover:bg-red-50"}`}
                          onClick={() => blockUser(u, !u.blocked)} disabled={u.role === "admin"}
                          data-testid={`admin-block-${u.id}`} title={u.blocked ? "Desbloquear" : "Bloquear"}>
                    {u.blocked ? <CheckCircle2 className="h-4 w-4" /> : <Ban className="h-4 w-4" />}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="contribuicoes" className="mt-4 space-y-3">
          <p className="text-sm text-slate-500">Contribuições sinalizadas automaticamente (fora da faixa ou valor suspeito) aguardando revisão.</p>
          {contribs.length === 0 && <p className="text-sm text-slate-500">Nenhuma contribuição sinalizada.</p>}
          {contribs.map((c) => (
            <Card key={c.id} className="border-amber-200 bg-amber-50" data-testid={`admin-contrib-${c.id}`}>
              <CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-medium">{c.service_name} {c.capacity && `· ${c.capacity} BTUs`}</p>
                  <p className="text-xs text-slate-600">{c.city}/{c.state} · <b className="font-mono-num">{brl(c.value)}</b> · motivo: {c.flagged_reason || "-"}</p>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={() => review(c, "approved")} data-testid={`approve-${c.id}`}>Aprovar</Button>
                  <Button size="sm" variant="outline" className="text-red-600 border-red-200" onClick={() => review(c, "rejected")} data-testid={`reject-${c.id}`}>Rejeitar</Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="parametros" className="mt-4">
          {params && (
            <Card className="border-slate-200 max-w-xl">
              <CardContent className="p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-sm">Base colaborativa ativa</p>
                    <p className="text-xs text-slate-500">Desative para pausar coleta e consultas</p>
                  </div>
                  <Switch checked={!!params.feature_enabled} onCheckedChange={(v) => setParams({ ...params, feature_enabled: v })} data-testid="param-enabled" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Mínimo de contribuições p/ exibir</Label>
                    <Input type="number" className="h-11 font-mono-num" data-testid="param-min" value={params.min_contributions} onChange={(e) => setParams({ ...params, min_contributions: e.target.value })} />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Validade dos dados (dias)</Label>
                    <Input type="number" className="h-11 font-mono-num" data-testid="param-validity" value={params.validity_days} onChange={(e) => setParams({ ...params, validity_days: e.target.value })} />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Corte da média aparada (%)</Label>
                    <Input type="number" className="h-11 font-mono-num" data-testid="param-trim" value={params.trim_pct} onChange={(e) => setParams({ ...params, trim_pct: e.target.value })} />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Fator de suspeição (× mediana)</Label>
                    <Input type="number" step="0.1" className="h-11 font-mono-num" data-testid="param-suspicion" value={params.suspicion_factor} onChange={(e) => setParams({ ...params, suspicion_factor: e.target.value })} />
                  </div>
                </div>
                <Button className="w-full h-11 bg-sky-600 hover:bg-sky-700 text-white" onClick={saveParams} data-testid="param-save-btn">Salvar parâmetros</Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="planos" className="mt-4 space-y-3">
          <p className="text-sm text-slate-500">Altere preços e limites. Novos preços valem para assinaturas criadas após a alteração — assinaturas ativas mantêm o valor contratado no Mercado Pago.</p>
          {plans.map((p, i) => (
            <Card key={p.id || p._id} className="border-slate-200" data-testid={`admin-plan-card-${p.id || p._id}`}>
              <CardContent className="p-4 flex flex-wrap items-end gap-3">
                <div className="flex-1 min-w-32">
                  <p className="font-semibold text-sm">{p.name}</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Mensal (R$)</Label>
                  <Input type="number" step="0.01" className="h-11 w-32 font-mono-num" data-testid={`plan-monthly-${p.id || p._id}`}
                         value={p.price_monthly} onChange={(e) => setPlans(plans.map((x, xi) => xi === i ? { ...x, price_monthly: e.target.value } : x))} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Anual (R$)</Label>
                  <Input type="number" step="0.01" className="h-11 w-32 font-mono-num" data-testid={`plan-yearly-${p.id || p._id}`}
                         value={p.price_yearly} onChange={(e) => setPlans(plans.map((x, xi) => xi === i ? { ...x, price_yearly: e.target.value } : x))} />
                </div>
                <Button className="h-11 bg-sky-600 hover:bg-sky-700 text-white" onClick={() => savePlan(p)} data-testid={`plan-save-${p.id || p._id}`}>Salvar</Button>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="diretorio" className="mt-4 space-y-3">
          {profiles.length === 0 && <p className="text-sm text-slate-500">Nenhum perfil no diretório.</p>}
          {profiles.map((p) => (
            <Card key={p.id} className="border-slate-200" data-testid={`admin-profile-${p.id}`}>
              <CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="font-medium text-sm">{p.trade_name} {p.active ? <Badge className="ml-1 bg-green-100 text-green-700 border-green-200 text-[10px]">ATIVO</Badge> : <Badge variant="outline" className="ml-1 text-[10px]">oculto</Badge>}</div>
                  <p className="text-xs text-slate-500">{p.city}/{p.state} · moderação: {p.moderation}</p>
                </div>
                {p.moderation === "ok" ? (
                  <Button size="sm" variant="outline" className="text-red-600 border-red-200" onClick={() => moderate(p, "suspenso")} data-testid={`suspend-${p.id}`}>Suspender</Button>
                ) : (
                  <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={() => moderate(p, "ok")} data-testid={`restore-${p.id}`}>Restaurar</Button>
                )}
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="materiais" className="mt-4 space-y-4">
          <Card className="border-slate-200">
            <CardHeader><CardTitle className="text-base">Materiais sugeridos aguardando validação ({pendingProducts.length})</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {pendingProducts.length === 0 && <p className="text-sm text-slate-500">Nenhum material pendente.</p>}
              {pendingProducts.map((p) => (
                <div key={p.id} className="border border-slate-200 rounded-lg p-3 flex flex-wrap items-center justify-between gap-3" data-testid={`admin-product-pending-${p.id}`}>
                  <div>
                    <p className="text-sm font-medium">{p.name}</p>
                    <p className="text-xs text-slate-500">{p.category} · unidade: {p.unit}{p.notes ? ` · ${p.notes}` : ""}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={() => reviewProduct(p, "validada")} data-testid={`approve-product-${p.id}`}>Aprovar</Button>
                    <Button size="sm" variant="outline" className="text-red-600 border-red-200" onClick={() => reviewProduct(p, "rejeitada")} data-testid={`reject-product-${p.id}`}>Rejeitar</Button>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardHeader><CardTitle className="text-base">Contribuições de preço em análise ({prodContribs.length})</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {prodContribs.length === 0 && <p className="text-sm text-slate-500">Nenhuma contribuição em análise.</p>}
              {prodContribs.map((c) => (
                <div key={c.id} className="border border-amber-200 bg-amber-50 rounded-lg p-3 flex flex-wrap items-center justify-between gap-3" data-testid={`admin-pc-${c.id}`}>
                  <div>
                    <p className="text-sm font-medium">{c.product_name}</p>
                    <p className="text-xs text-slate-600">{c.city}/{c.state} · <b className="font-mono-num">{brl(c.price)}</b>/{c.unit || "un."} · {fmtDate(c.created_at)}{c.supplier ? ` · ${c.supplier}` : ""}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={() => reviewProdContrib(c, "validada")} data-testid={`approve-pc-${c.id}`}>Aprovar</Button>
                    <Button size="sm" variant="outline" className="text-red-600 border-red-200" onClick={() => reviewProdContrib(c, "rejeitada")} data-testid={`reject-pc-${c.id}`}>Rejeitar</Button>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          {matRules && (
            <Card className="border-slate-200 max-w-xl">
              <CardHeader><CardTitle className="text-base">Regras do kit de instalação</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Sobra de cabo PP (m)</Label>
                    <Input type="number" step="0.5" className="h-11 font-mono-num" data-testid="rule-cabo-extra" value={matRules.cabo_pp_extra_m} onChange={(e) => setMatRules({ ...matRules, cabo_pp_extra_m: e.target.value })} />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Barra de isolamento (m)</Label>
                    <Input type="number" step="0.5" className="h-11 font-mono-num" data-testid="rule-barra-iso" value={matRules.barra_isolamento_m} onChange={(e) => setMatRules({ ...matRules, barra_isolamento_m: e.target.value })} />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Fixadores p/ condensadora</Label>
                    <Input type="number" className="h-11 font-mono-num" data-testid="rule-fixadores" value={matRules.fixadores_suporte_condensadora} onChange={(e) => setMatRules({ ...matRules, fixadores_suporte_condensadora: e.target.value })} />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Rolos de fita de acabamento</Label>
                    <Input type="number" className="h-11 font-mono-num" data-testid="rule-fita" value={matRules.fita_acabamento_rolos} onChange={(e) => setMatRules({ ...matRules, fita_acabamento_rolos: e.target.value })} />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Nota do cabo PP (exibida no kit)</Label>
                  <Input className="h-11" data-testid="rule-cabo-nota" value={matRules.cabo_pp_nota || ""} onChange={(e) => setMatRules({ ...matRules, cabo_pp_nota: e.target.value })} />
                </div>
                <Button className="w-full h-11 bg-sky-600 hover:bg-sky-700 text-white" onClick={saveMatRules} data-testid="rules-save-btn">Salvar regras</Button>
              </CardContent>
            </Card>
          )}

          <Card className="border-slate-200 max-w-xl">
            <CardHeader><CardTitle className="text-base">Ranking de colaboradores</CardTitle></CardHeader>
            <CardContent className="space-y-1.5">
              {leaderboard.length === 0 && <p className="text-sm text-slate-500">Nenhum ponto distribuído ainda.</p>}
              {leaderboard.slice(0, 10).map((r, i) => (
                <p key={r.user_id} className="text-sm text-slate-600 flex justify-between border-b border-slate-100 pb-1" data-testid={`leader-${i}`}>
                  <span>{i + 1}º · {r.name}</span>
                  <span className="font-mono-num">{r.points} pts</span>
                </p>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="custodia" className="mt-4 space-y-4">
          {escrowCfg && (
            <Card className="border-slate-200 max-w-xl">
              <CardContent className="p-4 flex items-end gap-3 flex-wrap">
                <div className="space-y-1.5">
                  <Label className="text-xs">Comissão da plataforma no GEPR Assist (%)</Label>
                  <Input type="number" step="0.5" min="0" max="50" className="h-11 w-32 font-mono-num" data-testid="escrow-fee" value={escrowCfg.fee_pct} onChange={(e) => setEscrowCfg({ ...escrowCfg, fee_pct: e.target.value })} />
                </div>
                <Button className="h-11 bg-sky-600 hover:bg-sky-700 text-white" onClick={saveEscrowCfg} data-testid="escrow-fee-save">Salvar</Button>
              </CardContent>
            </Card>
          )}

          <Card className="border-slate-200">
            <CardHeader><CardTitle className="text-base">Repasses pendentes ({payouts.length})</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {payouts.length === 0 && <p className="text-sm text-slate-500">Nenhum repasse aguardando.</p>}
              {payouts.map((p) => (
                <div key={p.id} className="border border-slate-200 rounded-lg p-3 flex flex-wrap items-center justify-between gap-3" data-testid={`payout-${p.id}`}>
                  <div>
                    <p className="text-sm font-medium">{p.expert_name} · <b className="font-mono-num">{brl(p.expert_amount)}</b></p>
                    <p className="text-xs text-slate-500">{p.request_title} · Pix: <b>{p.pix_key || "não informada"}</b> · comissão GEPR {brl(p.platform_fee)}</p>
                  </div>
                  <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={() => markPaid(p)} data-testid={`payout-paid-${p.id}`}>Confirmar repasse Pix</Button>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardHeader><CardTitle className="text-base">Pagamentos GEPR Assist ({escrowPay.length})</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {escrowPay.length === 0 && <p className="text-sm text-slate-500">Nenhum pagamento ainda.</p>}
              {escrowPay.map((p) => (
                <div key={p.id} className="border border-slate-200 rounded-lg p-3 flex flex-wrap items-center justify-between gap-3" data-testid={`escrow-${p.id}`}>
                  <div>
                    <p className="text-sm font-medium">{p.buyer_name} → {p.expert_name} · <b className="font-mono-num">{brl(p.amount)}</b></p>
                    <p className="text-xs text-slate-500">{fmtDate(p.created_at)} · MP {p.mp_payment_id || "-"} · {ESCROW_LABEL[p.status] || p.status}</p>
                  </div>
                  {p.status === "pago_retido" && (
                    <Button size="sm" variant="outline" className="text-red-600 border-red-200" onClick={() => refundEscrow(p)} data-testid={`escrow-refund-${p.id}`}>Reembolsar</Button>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="assist" className="mt-4 space-y-2">
          <p className="text-sm text-slate-500">Especialistas do GEPR Assist: verificação de confiança e moderação de perfis.</p>
          {assistSpecs.length === 0 && <p className="text-sm text-slate-500">Nenhum especialista cadastrado.</p>}
          {assistSpecs.map((s) => (
            <Card key={s.id} className="border-slate-200" data-testid={`admin-spec-${s.id}`}>
              <CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="font-medium text-sm">{s.name} {!!s.verified && <Badge className="ml-1 bg-sky-100 text-sky-700 border-sky-200 text-[10px]">VERIFICADO</Badge>} {!s.active && <Badge variant="outline" className="ml-1 text-[10px]">inativo</Badge>}</div>
                  <p className="text-xs text-slate-500">{[s.city, s.state].filter(Boolean).join("/") || "remoto"} · ★ {(s.rating_avg || 0).toFixed(1)} ({s.rating_count || 0}) · {s.sessions_completed || 0} atendimentos · {brl(s.price_session || 0)}/sessão</p>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Button size="sm" variant={s.verified ? "outline" : "default"} className={s.verified ? "" : "bg-sky-600 hover:bg-sky-700 text-white"} onClick={() => toggleVerify(s, !s.verified)} data-testid={`assist-verify-${s.id}`}>{s.verified ? "Remover verificação" : "Verificar"}</Button>
                  <Button size="sm" variant="outline" className={s.active ? "text-red-600 border-red-200" : "text-green-600 border-green-200"} onClick={() => toggleSpecActive(s, !s.active)} data-testid={`assist-active-${s.id}`}>{s.active ? "Desativar" : "Reativar"}</Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="disputas" className="mt-4 space-y-4">
          <Card className="border-slate-200">
            <CardHeader><CardTitle className="text-base">Disputas de pagamento GEPR Assist ({disputes.filter((d) => ["aberta", "em_analise"].includes(d.status)).length} em aberto)</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {disputes.length === 0 && <p className="text-sm text-slate-500">Nenhuma disputa registrada.</p>}
              {disputes.map((d) => (
                <div key={d.id} className="border border-slate-200 rounded-lg p-3 space-y-2" data-testid={`dispute-admin-${d.id}`}>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <p className="text-sm font-medium">{d.buyer_name} ↔ {d.expert_name} · <b className="font-mono-num">{brl(d.amount)}</b></p>
                    <Badge variant="outline" className={`text-[10px] ${d.status === "aberta" ? "bg-red-50 text-red-700 border-red-200" : d.status === "em_analise" ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-slate-100 text-slate-600"}`}>{DISPUTE_LABEL[d.status] || d.status}</Badge>
                  </div>
                  <p className="text-xs text-slate-500">Motivo: <b>{d.reason_name}</b> · aberta por {d.opened_by_name} · {fmtDate(d.created_at)}</p>
                  <p className="text-sm text-slate-600">{d.description}</p>
                  {(d.evidence || []).length > 0 && (
                    <p className="text-xs text-slate-500">Evidências: {d.evidence.map((e, i) => <a key={i} href={e.url} target="_blank" rel="noreferrer" className="text-sky-700 underline mr-2" data-testid={`dispute-ev-${d.id}-${i}`}>{e.name || e.url}</a>)}</p>
                  )}
                  {d.resolution_note && <p className="text-xs text-green-800 bg-green-50 border border-green-200 rounded p-2">Resolução: {d.resolution_note}</p>}
                  {["aberta", "em_analise"].includes(d.status) && d.payment_status === "pago_retido" && (
                    <div className="flex gap-2 flex-wrap">
                      {d.status === "aberta" && <Button size="sm" variant="outline" className="h-9" onClick={() => resolveDispute(d, "em_analise")} data-testid={`dispute-analise-${d.id}`}>Em análise</Button>}
                      <Button size="sm" className="h-9 bg-red-600 hover:bg-red-700 text-white" onClick={() => resolveDispute(d, "reembolso")} data-testid={`dispute-refund-${d.id}`}>Reembolsar comprador</Button>
                      <Button size="sm" className="h-9 bg-green-600 hover:bg-green-700 text-white" onClick={() => resolveDispute(d, "liberacao")} data-testid={`dispute-release-${d.id}`}>Liberar ao especialista</Button>
                      <Button size="sm" variant="ghost" className="h-9" onClick={() => resolveDispute(d, "encerrar")} data-testid={`dispute-close-${d.id}`}>Encerrar sem ação</Button>
                    </div>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardHeader><CardTitle className="text-base">Infrações anti-bypass ({infractions.length} usuários)</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              <p className="text-xs text-slate-500">Telefones e e-mails enviados no chat são mascarados automaticamente. A partir de 3 infrações o usuário é sinalizado; você pode suspender o chat.</p>
              {infractions.length === 0 && <p className="text-sm text-slate-500">Nenhuma infração registrada.</p>}
              {infractions.map((u) => (
                <div key={u.user_id} className="border border-slate-200 rounded-lg p-3 flex flex-wrap items-center justify-between gap-3" data-testid={`infraction-${u.user_id}`}>
                  <div>
                    <div className="text-sm font-medium">{u.name} {!!u.chat_flagged && <Badge className="ml-1 bg-amber-100 text-amber-700 border-amber-200 text-[10px]">SINALIZADO</Badge>} {!!u.chat_blocked && <Badge className="ml-1 bg-red-100 text-red-700 border-red-200 text-[10px]">CHAT SUSPENSO</Badge>}</div>
                    <p className="text-xs text-slate-500">{u.email} · {u.total} infração(ões) · última em {fmtDate(u.last_at)}</p>
                  </div>
                  <Button size="sm" variant="outline" className={u.chat_blocked ? "text-green-600 border-green-200" : "text-red-600 border-red-200"} onClick={() => toggleChatBlock(u, !u.chat_blocked)} data-testid={`chat-block-${u.user_id}`}>
                    {u.chat_blocked ? "Reativar chat" : "Suspender chat"}
                  </Button>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="pagamentos" className="mt-4">
          <Card className="border-slate-200 max-w-2xl">
            <CardHeader><CardTitle className="text-base">Configuração de pagamento (Mercado Pago)</CardTitle></CardHeader>
            <CardContent className="text-sm text-slate-700 space-y-3">
              <p>Provedor: <b>Mercado Pago</b> · Modo: <b>{payInfo?.mode || "production"}</b> · Status: <b>{payInfo?.configured ? "configurado" : "pendente"}</b> · Métodos: <b>{(payInfo?.methods || []).join(", ")}</b></p>
              <ol className="list-decimal pl-5 space-y-1.5 text-slate-600">
                <li>Aplicação "GEPR - Gestão de Ordens de Serviço" criada no Mercado Pago Developers com credenciais de produção.</li>
                <li>Webhook a cadastrar em <b>Webhooks → Configurar notificações</b>: <code className="bg-slate-100 px-1 rounded">{payInfo?.webhook_url || "/api/mercadopago/webhook"}</code></li>
                <li>Eventos a marcar: payment, subscription_preapproval, subscription_authorized_payment {payInfo?.webhook_secret_set ? "· secret configurado ✅" : "· secret ainda não configurado ⚠️"}</li>
                <li>Assinaturas recorrentes via Preapproval (mensal/anual). Pix, cartão e boleto no checkout.</li>
                <li>Teste E2E: fazer uma assinatura real de valor baixo e validar a ativação automática do plano.</li>
              </ol>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
